/**
 * Block-level and inline (mark) styling for the rich text editor's DOM
 * overlay and RenderContent's read-mode renderer, so editing and reading
 * render identically.
 *
 * Ported from teacher-mobile/src/screens/studio/editor/richEditor/richText.ts
 * + blockStyle.ts, translated from React Native TextStyle objects to DOM
 * CSS style objects / Tailwind classNames (this codebase's convention).
 */

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// Matches teacher-mobile's TEXT_COLORS/BG_COLORS (blocks.ts) — kept here so
// legacy <c:key>/<hl:key> marks from mobile-authored content still render
// with the right color, even though Milestone 1 exposes no web toolbar
// button to create new ones. BG_COLOR_HEX matches the desktop highlight
// palette (frontend/src/components/teacher/ContentEditor/ContentEditor.jsx)
// that mobile's own BG_COLORS was already updated to match.
const TEXT_COLOR_HEX = { red: '#EF4444', orange: '#F97316', yellow: '#D97706', green: '#16A34A', blue: '#2563EB', purple: '#7C3AED' };
const BG_COLOR_HEX = { yellow: '#FDE047', green: '#86EFAC', blue: '#93C5FD', pink: '#FCA5A5', purple: '#C4B5FD' };

function styleForMarks(active) {
  const s = {};
  let underline = false;
  let strike = false;
  for (const m of active) {
    if (m.kind === 'bold') s.fontWeight = 700;
    if (m.kind === 'italic') s.fontStyle = 'italic';
    if (m.kind === 'underline') underline = true;
    if (m.kind === 'strike') strike = true;
    if (m.kind === 'code') { s.fontFamily = MONO; s.background = 'rgba(0,0,0,0.05)'; s.padding = '0 3px'; s.borderRadius = '3px'; }
    if (m.kind === 'equation') { s.fontStyle = 'italic'; s.color = '#0D9488'; }
    if (m.kind === 'color' && m.param && TEXT_COLOR_HEX[m.param]) s.color = TEXT_COLOR_HEX[m.param];
    if (m.kind === 'bg' && m.param && BG_COLOR_HEX[m.param]) s.backgroundColor = BG_COLOR_HEX[m.param];
  }
  if (underline && strike) s.textDecoration = 'underline line-through';
  else if (underline) s.textDecoration = 'underline';
  else if (strike) s.textDecoration = 'line-through';
  return s;
}

/** Turns plain text + (possibly overlapping) format ranges into a flat list
 * of non-overlapping styled runs. Since the text is always plain (no
 * markdown tokens), this is a straight character-for-character split — no
 * hidden markers, no reserved gaps. */
export function segmentsFromMarks(text, marks) {
  if (!text) return [];
  if (!marks.length) return [{ text, style: {} }];

  const cuts = new Set([0, text.length]);
  marks.forEach((m) => { cuts.add(m.start); cuts.add(m.end); });
  const points = Array.from(cuts).sort((a, b) => a - b);

  const segments = [];
  for (let i = 0; i < points.length - 1; i++) {
    const s = points[i];
    const e = points[i + 1];
    if (s >= e) continue;
    const active = marks.filter((m) => m.start <= s && m.end >= e);
    segments.push({ text: text.slice(s, e), style: styleForMarks(active) });
  }
  return segments;
}

/** Tailwind classNames for a block's type — headings/quote/paragraph. */
export function blockClassFor(type) {
  switch (type) {
    case 'h1': return 'text-2xl font-extrabold text-gray-900';
    case 'h2': return 'text-xl font-extrabold text-gray-900';
    case 'h3': return 'text-lg font-bold text-gray-900';
    case 'h4': return 'text-base font-bold text-gray-900';
    case 'quote': return 'italic text-gray-500';
    default: return 'text-[15px] text-gray-700';
  }
}
